import { Component, EventEmitter, Input, NgZone, OnInit, Output, inject } from '@angular/core';

import { BytelTagService } from '@base/services/bytel-tag.service';
import { ViewService } from '@base/services/view.service';

import { PartnerProduct } from '@catalog/products/interface/phone';

import { JsonProduct } from '@app/baseBtl/catalog/products/interface/context';
import { MobileRecoveryService } from '@app/modules/mobile-recovery/mobile-recovery.service';
import { RecoveryStatus } from '@app/modules/mobile-recovery/modal-recovery/interface/modal-recovery-model';

import { Observable, Subject, takeUntil } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, mergeMap } from 'rxjs/operators';

@Component({
    selector: 'btl-modal-recovery-search',
    templateUrl: './modal-recovery-search.component.html',
    styleUrls: ['./modal-recovery-search.component.scss'],
})
export class ModalRecoverySearchComponent implements OnInit {
    @Output() goToNextStep = new EventEmitter<void>();
    @Input() forfait_bonus_reprise: string;
    public typeaheadSearchInput: (text$: Observable<string>) => Observable<PartnerProduct[]>;
    public typeaheadInputFormatter = (phone: { nom: string }): string => phone.nom;

    private _scavenger = new Subject();
    private product: JsonProduct;

    public hasBonus: boolean;
    public bonus: number;
    public modelName: string;
    public searchModel: string | PartnerProduct;
    public selectedPhone: PartnerProduct;
    public isWithoutTax: boolean;
    public sliderOptions = {
        pageDots: true,
        prevNextButtons: false,
        wrapAround: false,
        contain: true,
        adaptiveHeight: true,
    };
    public isMobileMQ: boolean = false;

    private readonly MOBILE_BREAKPOINT: string = '(max-width: 768px)';

    private mobileRecoveryService: MobileRecoveryService = inject(MobileRecoveryService);
    private viewService: ViewService = inject(ViewService);
    private tagService: BytelTagService = inject(BytelTagService);
    private ngZone: NgZone = inject(NgZone);

    ngOnInit(): void {
        this._initBreakpointMonitoring();
        this.product = this.mobileRecoveryService.parentProduct;
        this.modelName = this.product.name;
        this.computeBonus();

        this.selectedPhone = this.mobileRecoveryService.getItem('recoverySelectedPhone');

        if (!!this.selectedPhone) {
            this.searchModel = this.selectedPhone;
        }

        this.isWithoutTax = this.viewService.canApplyWithoutTaxSignal();

        this.typeaheadSearchInput = (text$): Observable<PartnerProduct[]> =>
            text$.pipe(
                debounceTime(200),
                distinctUntilChanged(),
                mergeMap((searchText) => {
                    this.selectedPhone = undefined;
                    this.mobileRecoveryService.removeItem('recoverySelectedPhone');
                    this.mobileRecoveryService.removeItem(this.mobileRecoveryService.MOBILE_RECOVERY_STEP_STORAGE_KEY);

                    return searchText.length > 2 ? this.mobileRecoveryService.getProduitsPartenaire(searchText) : [];
                }),
                map((result) => result.items),
            );

        this.sendTag();
    }

    public ngOnDestroy(): void {
        // Used for scavenger observable unsubscriptions
        this._scavenger.next(null);
        this._scavenger.complete();
    }

    public clearSearch(): void {
        this.searchModel = '';
        this.selectedPhone = undefined;
        this.mobileRecoveryService.removeItem('recoverySelectedPhone');
        this.mobileRecoveryService.setRecoveryStatus(RecoveryStatus.UNSTARTED);
    }

    public computeBonus(): void {
        this.hasBonus = this.mobileRecoveryService.hasBonus();
        this.bonus = this.mobileRecoveryService.getBonus();
    }

    public next(): void {
        this.mobileRecoveryService.setRecoveryStatus(RecoveryStatus.UNFINISHED);
        this.goToNextStep.emit();
    }

    public selectedItem(selected: { item: PartnerProduct }): void {
        this.mobileRecoveryService
            .getProduitsPartenaireCriteres(selected.item.id)
            .pipe(takeUntil(this._scavenger))
            .subscribe();
        this.selectedPhone = selected.item;
        this.mobileRecoveryService.setRecoveryStatus(RecoveryStatus.ONGOING);
        this.mobileRecoveryService.setItem('recoverySelectedPhone', this.selectedPhone);
    }

    public sendTag(): void {
        this.tagService.send({
            page: 'E1_Infos_modele_mobile',
            pageCategorie: 'Formulaire',
        });
    }

    private _initBreakpointMonitoring(): void {
        const mq: MediaQueryList = window.matchMedia(this.MOBILE_BREAKPOINT);
        this.isMobileMQ = mq.matches;

        mq.addEventListener('change', (evt) => {
            if (evt.matches !== this.isMobileMQ) {
                this.ngZone.run(() => {
                    this.isMobileMQ = evt?.matches;
                });
            }
        });
    }
}
